import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import resolveImageUrl from '../../utils/resolveImageUrl';
import {
  formatMoney,
  formatRentalDate,
  getBookedFor,
  getBookedFrom,
  getDropoffTime,
  getRentalDays,
  getRentalLineTotal,
} from '../../utils/rentalFields';
import { showAppToast } from '../../utils/notifications';
import { trackBookingConfirmation } from '../../utils/analytics';

function Checkout({ cartItems, totalCost, onResetCart }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const items = useMemo(() => cartItems || [], [cartItems]);
  const subtotal = useMemo(() => {
    if (items.length) {
      return items.reduce((sum, item) => sum + getRentalLineTotal(item), 0);
    }
    return Number(totalCost || 0);
  }, [items, totalCost]);
  const insurance = items.length ? 50 : 0;
  const tax = subtotal * 0.1;
  const total = subtotal + insurance + tax;

  const handlePayment = async () => {
    if (!items.length) {
      showAppToast({
        message: 'Your cart is empty',
        type: 'warning',
      });
      navigate('/vehicles');
      return;
    }

    setLoading(true);
    try {
      // FIX-05/FIX-20: Simulated payment processing — in production, integrate Stripe
      // This is a placeholder that mimics a payment gateway redirect
      await new Promise((resolve) => window.setTimeout(resolve, 900));
      showAppToast({
        message: "Order placed successfully. Your rental has been confirmed and we'll send you a confirmation email shortly.",
        type: 'success',
        duration: 3400,
      });
      trackBookingConfirmation(items, total);
      onResetCart();
      navigate('/payment/success');
    } catch (error) {
      showAppToast({
        message: error.message || 'Failed to place order. Please try again.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="booking-page px-4 py-10">
      <div className="mx-auto max-w-6xl">
        <div className="checkout-hero card-luxury text-center">
          <h1 className="section-title text-4xl text-primary-theme">You will be redirected to payment page</h1>
          <p className="mt-3 text-secondary-theme">
            Review your vehicle booking details before making payment.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <section className="space-y-4 lg:col-span-2">
            {items.length ? (
              items.map((item) => {
                const product = item.product || {};
                const days = getRentalDays(item);
                return (
                  <article key={item.id} className="checkout-item card-luxury">
                    <img
                      src={resolveImageUrl(product.imageURL)}
                      alt={product.name || 'Vehicle'}
                      className="checkout-item-image"
                    />
                    <div className="flex-1">
                      <h2 className="card-title text-2xl text-primary-theme">{product.name}</h2>
                      <div className="checkout-meta">
                        <span>{formatMoney(product.price)} / day</span>
                        <span>{days} day{days === 1 ? '' : 's'}</span>
                        <span>Pickup: {formatRentalDate(getBookedFrom(item))}</span>
                        <span>Dropoff: {formatRentalDate(getBookedFor(item))}</span>
                        <span>Dropoff time: {getDropoffTime(item) || 'Not selected'}</span>
                      </div>
                    </div>
                    <strong className="text-xl text-accent">{formatMoney(getRentalLineTotal(item))}</strong>
                  </article>
                );
              })
            ) : (
              <div className="card-luxury p-8 text-center">
                <h2 className="section-title text-2xl text-primary-theme">No bookings to checkout</h2>
                <p className="mt-3 text-secondary-theme">Choose a vehicle before making payment.</p>
              </div>
            )}
          </section>

          <aside className="lg:col-span-1">
            <div className="booking-summary card-luxury">
              <h2 className="section-title text-2xl text-primary-theme">Payment Summary</h2>
              <div className="mt-6 space-y-4">
                <div className="flex justify-between text-secondary-theme">
                  <span>Subtotal</span>
                  <span>{formatMoney(subtotal)}</span>
                </div>
                <div className="flex justify-between text-secondary-theme">
                  <span>Insurance</span>
                  <span>{formatMoney(insurance)}</span>
                </div>
                <div className="flex justify-between text-secondary-theme">
                  <span>Tax</span>
                  <span>{formatMoney(tax)}</span>
                </div>
                <div className="border-t border-luxury pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-primary-theme">Total</span>
                    <span className="text-3xl font-bold text-accent">{formatMoney(total)}</span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handlePayment}
                disabled={loading || !items.length}
                className="btn-luxury mt-6 w-full"
              >
                {loading ? 'Processing...' : 'Make Payment'}
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default Checkout;
